import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { startCase } from "lodash";
import { AiFillEye } from "react-icons/ai";
import FormViewer from './FormViewer'; // Import the new component
import AuthContext from '../../context/AuthContext'
import DocumentViewer from "./DocumentViewer";

const labelText = (value, fallback = "—") => {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  return raw.replace(/[-_]/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
};

function SectionTable({ title, rows }) {
  if (!rows.length) return null;
  return (
    <div className="mb-5">
      <h3 className="text-base font-semibold mb-2">{title}</h3>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full table-fixed text-xs text-left text-slate-600">
          <colgroup>
            <col className="w-1/5" />
            <col className="w-1/5" />
            <col className="w-1/4" />
            <col className="w-1/4" />
          </colgroup>
          <thead className="bg-slate-50 text-xs text-slate-700 border-b border-slate-200">
            <tr>
              <th className="py-3 px-4 text-center">Sr. No.</th>
              <th className="py-3 px-4 text-left">Document</th>
              <th className="py-3 px-4 text-left">Category</th>
              <th className="py-3 px-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.key} className="bg-white border-b border-slate-200 hover:bg-gray-50">
                <td className="py-3 px-4 text-center text-gray-900">{index + 1}</td>
                <td className="py-3 px-4 text-left text-gray-900">{row.name}</td>
                <td className="py-3 px-4 text-left text-gray-900">{row.category}</td>
                <td className="py-3 px-4 text-center">
                  <button
                    type="button"
                    title="View document"
                    onClick={row.onView}
                    className="w-8 h-8 inline-flex items-center justify-center rounded border border-blue-100 bg-blue-50 text-[#2f5bff]"
                  >
                    <AiFillEye className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TotalAcceptedView = ({ setAcceptedShowModal, item }) => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext)
  const [open, setOpen] = useState(false);
  const [contentFilled, setContentFilled] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const handleView = (id) => {
    const form = item?.EmployeeFilledForms[0].find(form => form._id === id);
    if (form && form.formData && form.content) {
      setSelectedForm({
        structure: form.formData,
        data: form
      });
      setOpen(true);
    }
  };

  const handleCloseForm = () => {
    setOpen(false);
    setSelectedForm(null);
  };

  const getShortId = (name, id) =>
    `${name?.slice(0, 3) ?? ''}${id?.slice(-3) ?? ''}`

  const uploadedDocuments = Array.isArray(item?.EmployeeDocuments) ? item.EmployeeDocuments : [];
  const documentsByCategory = uploadedDocuments.reduce((groups, doc, index) => {
    const category = labelText(typeof doc.category === "string" ? doc.category : doc.category?.name, "Document");
    if (!groups[category]) groups[category] = [];
    groups[category].push({
      key: doc._id || index,
      name: labelText(doc.title || doc.originalName || doc.fileName, "Document"),
      category,
      onView: () => setSelectedDocument(doc),
    });
    return groups;
  }, {});

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto max-w-3xl">
          <form className="sm:py-4 sm:px-2 py-8 px-7">
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[940px] max-h-[85vh] overflow-y-auto	">
              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                <h3 className="text-xl font-semibold">{t("O_VIEW_USER")}</h3>
                <button type="button" className=" ml-auto flex items-center justify-center  text-black border-2 rounded-full  h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none" onClick={() => setAcceptedShowModal(false)}>
                  <span className=" text-[#B8BBBF]  text-4xl " title="Close">
                    ×
                  </span>
                </button>
              </div>
              <div className="relative p-6 flex-auto">
                <h3 className="text-xl font-semibold">Employees details</h3>
                <div className="grid grid-cols-3">
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>Employee ID</strong>
                      <h6>{item?.employmentStatus === "terminated" ? getShortId(user?.name, item?.Employee?._id) : item?.Employee?.employeeId || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>Employee name</strong>
                      <h6>{item?.Employee?.name || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>Email ID</strong>
                      <h6>{item?.Employee?.email || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>Year of experience</strong>
                      <h6>{item?.yearOfExperiece || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>DOB</strong>
                      <h6>{dayjs(item?.Employee?.dob).format("MM-DD-YYYY")}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-6 w-full group">
                      <strong>Country</strong>
                      <h6>{item?.EmployeeCountry?.name || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-6 w-full group">
                      <strong>State</strong>
                      <h6>{item?.EmployeeState?.name || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-6 w-full group">
                      <strong>City</strong>
                      <h6>{item?.Employee?.city || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-6 w-full group">
                      <strong>Skill selected by employee</strong>
                      <h6> {item.EmployeeSkills?.map((items) => items?.name).join(", ") || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-6 w-full group">
                      <strong>Job title</strong>
                      <h6>{item?.Job?.name || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-6 w-full group">
                      <strong>Department</strong>
                      <h6>{item?.JobDepartment?.name || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-6 w-full group">
                      <strong>Employment length [in days]</strong>
                      <h6>{item?.Job?.employmentLength || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-6 w-full group">
                      <strong>Skill uploaded in job</strong>
                      <h6> {item.JobSkills?.map((value) => value?.name).join(", ") || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>Duration type</strong>
                      <h6>{item.duration || ""}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>Status</strong>
                      <h6>{startCase(item.employmentStatus || "")}</h6>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>LinkedIn</strong>
                      <h6>{item.Employee?.linkedInLink ? (
                        <a href={item.Employee.linkedInLink} target="_blank" rel="noopener noreferrer">
                          View Profile
                        </a>
                      ) : (
                        <span>LinkedIn profile not added</span>
                      )}</h6>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative px-6 pb-2">
                <h3 className="text-xl font-semibold">Documents uploaded by the employee</h3>
                {uploadedDocuments.length > 1 ?<p className="text-sm text-gray-500 mt-1 mb-4">
                  {uploadedDocuments.length} uploaded {uploadedDocuments.length === 1 ? "document" : "documents"}
                </p> : null}
                {uploadedDocuments.length > 0 ? (
                  Object.entries(documentsByCategory).map(([category, rows]) => (
                    <SectionTable key={category} title={category} rows={rows} />
                  ))
                ) : (
                  <p className="text-sm text-gray-500 mb-4">No documents uploaded by this employee.</p>
                )}
              </div>
              <div className="flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b">
                <button type="button" className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150" onClick={() => setAcceptedShowModal(false)}>
                  {t("O_BACK")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      {/* <div className="opacity-25 fixed inset-0 z-40 bg-black" /> */}
      {/* Form Viewer Modal */}
      {open && selectedForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-[960px] max-h-[90vh]">
            <FormViewer
              formStructure={selectedForm.structure}
              formData={selectedForm.data}
              onClose={handleCloseForm}
            />
          </div>
        </div>
      )}


      {selectedDocument && (
  <DocumentViewer
    document={selectedDocument}
    onClose={() => setSelectedDocument(null)}
  />
)}
    </>
  );
};

export default TotalAcceptedView;
